import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, List } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useSectionTracking } from '@/hooks/audio/useSectionTracking';
import { ScrollArea } from '@/components/ui/scroll-area';

interface SectionNavigationButtonsProps {
  content: string;
  currentWordIndex: number;
  duration: number;
  onSeek: (time: number) => void;
  isPlaying: boolean;
}

export const SectionNavigationButtons: React.FC<SectionNavigationButtonsProps> = ({
  content,
  currentWordIndex,
  duration,
  onSeek,
  isPlaying,
}) => {
  const { sections, currentSection, getSectionTimePosition, totalWords } = useSectionTracking(
    content,
    currentWordIndex
  );

  const currentSectionIndex = useMemo(() => {
    return sections.findIndex((s) => s === currentSection);
  }, [sections, currentSection]);

  const hasPrevious = currentSectionIndex > 0;
  const hasNext = currentSectionIndex < sections.length - 1;

  const goToPreviousSection = () => {
    if (hasPrevious) {
      const prevSection = sections[currentSectionIndex - 1];
      const timePosition = getSectionTimePosition(prevSection, totalWords, duration);
      onSeek(timePosition);
    }
  };

  const goToNextSection = () => {
    if (hasNext) {
      const nextSection = sections[currentSectionIndex + 1];
      const timePosition = getSectionTimePosition(nextSection, totalWords, duration);
      onSeek(timePosition);
    }
  };

  const jumpToSection = (section: typeof sections[0]) => {
    const timePosition = getSectionTimePosition(section, totalWords, duration);
    onSeek(timePosition);
  };

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">Section Navigation</label>
        <span className="text-xs text-muted-foreground">
          {currentSectionIndex + 1} of {sections.length}
        </span>
      </div>

      {/* Quick Navigation Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={goToPreviousSection}
          disabled={!hasPrevious || !isPlaying}
          variant="outline"
          size="sm"
          className="flex-1"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <Button
          onClick={goToNextSection}
          disabled={!hasNext || !isPlaying}
          variant="outline"
          size="sm"
          className="flex-1"
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* All Sections List */}
      <Collapsible>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full justify-start">
            <List className="h-4 w-4 mr-2" />
            View All Sections ({sections.length})
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <ScrollArea className="h-[200px] rounded-md border border-border/50 p-2">
            <div className="space-y-1">
              {sections.map((section, idx) => {
                const isCurrentSection = idx === currentSectionIndex;
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 p-2 rounded-md transition-colors ${
                      isCurrentSection
                        ? 'bg-primary/10 border-l-2 border-primary'
                        : 'hover:bg-secondary/50'
                    }`}
                  >
                    <Badge
                      variant={isCurrentSection ? 'default' : 'outline'}
                      className="shrink-0 text-xs"
                    >
                      H{section.level}
                    </Badge>
                    <span
                      className={`flex-1 text-sm truncate ${
                        isCurrentSection ? 'font-medium text-foreground' : 'text-muted-foreground'
                      }`}
                      title={section.title}
                    >
                      {section.title}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => jumpToSection(section)}
                      disabled={!isPlaying}
                      className="shrink-0 h-7 px-2 text-xs"
                    >
                      Jump
                    </Button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
